import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, brl, fmtDateTime, fromLocalInput, toLocalInput } from "@/lib/api";
import { offlineRequest } from "@/lib/offline";
import { useAuth, hasRole } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import StatusBadge from "@/components/StatusBadge";
import SignaturePad from "@/components/SignaturePad";
import PhotoUploader from "@/components/PhotoUploader";
import { EditWorkOrderDialog } from "@/components/EditWorkOrderDialog";
import { ArrowLeft, Lock, Plus, Trash2, CheckCircle2, Plane, Receipt, Undo2 } from "lucide-react";
import { toast } from "sonner";

const CHECKLIST = [
  ["estrutura_exterior", "Estrutura Exterior"],
  ["para_brisa", "Para-brisa"],
  ["cabine_interior", "Cabine Interior"],
  ["luzes_navegacao_pouso", "Luzes Navegação / Pouso"],
  ["pneus_trem_pouso", "Pneus / Trem de Pouso"],
  ["status_combustivel", "Status do Combustível"],
];

export default function WorkOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [wo, setWO] = useState(null);
  const [catalogServices, setCatalogServices] = useState([]);
  const [draftServices, setDraftServices] = useState([]);
  const [checklist, setChecklist] = useState(null);
  const [adhocName, setAdhocName] = useState("");
  const [closingRemarks, setClosingRemarks] = useState("");
  const [clientSig, setClientSig] = useState(null);
  const [opSig, setOpSig] = useState(null);
  const [finalPhotos, setFinalPhotos] = useState([]);
  const [movement, setMovement] = useState({ movement_type: "saida_temporaria_patio", datetime_iso: "", destination_purpose: "", estimated_return: "", notes: "" });

  const isOperator = user?.role === "operator";
  const canEditOperator = wo && ["EM_OPERACAO", "EM_ANDAMENTO"].includes(wo.status);
  const canEditOffice = wo && wo.status !== "FATURADO" && hasRole(user, "admin", "office");
  const canInvoice = wo && wo.status === "AGUARDANDO_FATURAMENTO" && hasRole(user, "admin", "office");

  const fetchAll = useCallback(async () => {
    const { data } = await api.get(`/work-orders/${id}`);
    setWO(data);
    setChecklist(data.checklist);
    setDraftServices(data.services || []);
    setClosingRemarks(data.closing_remarks || "");
    setClientSig(data.client_signature || null);
    setOpSig(data.operator_signature || null);
    setFinalPhotos(data.final_photos || []);
    const cat = await api.get("/services");
    setCatalogServices(cat.data);
  }, [id]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const total = useMemo(() => draftServices.reduce((acc, s) => acc + (Number(s.quantity) || 0) * (Number(s.unit_price) || 0), 0), [draftServices]);

  const saveChecklist = async () => {
    const res = await offlineRequest({
      method: "PUT",
      url: `/work-orders/${id}/checklist`,
      data: checklist,
      label: `Checklist OS ${wo.code}`,
    });
    if (res?.queued) toast.message("Salvo offline — sincronizará ao reconectar");
    else toast.success("Checklist salvo");
    if (!res?.queued) fetchAll();
  };

  const addCatalogService = (svc) => {
    if (draftServices.find((s) => s.service_id === svc.id)) return;
    setDraftServices((prev) => ([
      ...prev,
      {
        id: crypto.randomUUID(),
        service_id: svc.id,
        name: svc.name,
        quantity: 1,
        unit_price: isOperator ? 0 : (svc.unit_price || 0),
        is_ad_hoc: false,
      },
    ]));
  };

  const addAdHocService = () => {
    if (!adhocName.trim()) return;
    setDraftServices((prev) => ([
      ...prev,
      { id: crypto.randomUUID(), service_id: null, name: adhocName.trim(), quantity: 1, unit_price: 0, is_ad_hoc: true },
    ]));
    setAdhocName("");
  };

  const removeLine = (lineId) => setDraftServices((prev) => prev.filter((s) => s.id !== lineId));
  const setLine = (lineId, patch) => setDraftServices((prev) => prev.map((s) => s.id === lineId ? { ...s, ...patch } : s));

  const saveServices = async () => {
    const res = await offlineRequest({
      method: "PUT",
      url: `/work-orders/${id}/services`,
      data: { services: draftServices },
      label: `Serviços OS ${wo.code}`,
    });
    if (res?.queued) toast.message("Salvo offline — sincronizará ao reconectar");
    else toast.success("Serviços atualizados");
    if (!res?.queued) fetchAll();
  };

  const addMovement = async () => {
    if (!movement.datetime_iso) {
      toast.error("Informe data/hora da movimentação");
      return;
    }
    const payload = {
      ...movement,
      datetime_iso: fromLocalInput(movement.datetime_iso),
      estimated_return: movement.estimated_return ? fromLocalInput(movement.estimated_return) : null,
    };
    const res = await offlineRequest({
      method: "POST",
      url: `/work-orders/${id}/movements`,
      data: payload,
      label: `Movimentação ${wo.code}`,
    });
    setMovement({ movement_type: "saida_temporaria_patio", datetime_iso: "", destination_purpose: "", estimated_return: "", notes: "" });
    if (res?.queued) toast.message("Salvo offline — sincronizará ao reconectar");
    else { toast.success("Movimentação registrada"); fetchAll(); }
  };

  const finalize = async () => {
    if (!clientSig || !opSig) {
      toast.error("Capture as assinaturas do cliente e do operador");
      return;
    }
    const res = await offlineRequest({
      method: "POST",
      url: `/work-orders/${id}/finalize`,
      data: {
        closing_remarks: closingRemarks,
        final_photos: finalPhotos,
        client_signature: clientSig,
        operator_signature: opSig,
        actual_departure: new Date().toISOString(),
      },
      label: `Finalizar OS ${wo.code}`,
    });
    if (res?.queued) toast.message("Salvo offline — sincronizará ao reconectar");
    else { toast.success("OS enviada para faturamento"); fetchAll(); }
  };

  const invoiceNow = async () => {
    if (!window.confirm("Confirmar faturamento? Os valores serão travados.")) return;
    const { data } = await api.post(`/work-orders/${id}/invoice`);
    toast.success(`Faturado: ${data.invoice_number}`);
    fetchAll();
  };

  const revertInvoice = async () => {
    if (!window.confirm(`Estornar a nota ${wo.invoice_number}? A OS voltará para "Aguardando Faturamento" e o estorno ficará registrado para auditoria.`)) return;
    try {
      await api.post(`/work-orders/${id}/revert-invoice`);
      toast.success("Nota estornada — OS voltou para Aguardando Faturamento");
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao estornar");
    }
  };

  const deleteWO = async () => {
    if (!window.confirm(`Excluir permanentemente a OS ${wo.code}? Esta ação não pode ser desfeita.`)) return;
    try {
      await api.delete(`/work-orders/${id}`);
      toast.success("OS excluída");
      navigate("/");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao excluir");
    }
  };

  if (!wo) return <div className="p-8 text-slate-500">Carregando…</div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto pb-24">
      <Button variant="ghost" size="sm" className="mb-3 -ml-2" onClick={() => navigate("/")} data-testid="back-button">
        <ArrowLeft className="w-4 h-4 mr-1" /> Ordens
      </Button>

      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3 mb-6">
        <div>
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-slate-500 flex items-center gap-2">
            <span data-testid="wo-code">{wo.code}</span>
            <StatusBadge status={wo.status} />
          </div>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter text-slate-950 flex items-center gap-3">
            <Plane className="w-7 h-7 text-[#0052CC]" />
            <span className="font-mono uppercase" data-testid="wo-tail">{wo.tail_number}</span>
          </h1>
          <p className="text-sm text-slate-600 mt-1">{wo.aircraft_model || "—"} · {wo.client_name || "—"}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {canEditOffice && <EditWorkOrderDialog wo={wo} onSaved={fetchAll} />}
          {canInvoice && (
            <Button onClick={invoiceNow} className="h-11 bg-[#0052CC] hover:bg-[#003D99] text-white rounded-sm" data-testid="invoice-button">
              <Receipt className="w-4 h-4 mr-2" /> Faturar
            </Button>
          )}
          {wo.status === "FATURADO" && (
            <Button onClick={() => navigate(`/faturamento/${wo.id}/preview`)} variant="outline" className="h-11 rounded-sm" data-testid="view-invoice-button">
              <Receipt className="w-4 h-4 mr-2" /> Ver Nota
            </Button>
          )}
          {wo.status === "FATURADO" && hasRole(user, "admin") && (
            <Button onClick={revertInvoice} variant="outline" className="h-11 rounded-sm border-amber-300 text-amber-700 hover:bg-amber-500 hover:text-white hover:border-amber-500" data-testid="revert-invoice-button">
              <Undo2 className="w-4 h-4 mr-2" /> Estornar Nota
            </Button>
          )}
          {hasRole(user, "admin") && (
            <Button onClick={deleteWO} variant="outline" className="h-11 rounded-sm border-red-300 text-red-600 hover:bg-red-600 hover:text-white hover:border-red-600" data-testid="delete-wo-button">
              <Trash2 className="w-4 h-4 mr-2" /> Excluir
            </Button>
          )}
        </div>
      </div>

      {/* Metadata */}
      <Card className="p-5 rounded-sm border-slate-200 mb-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <Field label="Origem">{wo.flight_origin || "—"}</Field>
          <Field label="Destino">{wo.flight_destination || "—"}</Field>
          <Field label="Chegada">{fmtDateTime(wo.arrival_datetime)}</Field>
          <Field label="Partida Estimada">{fmtDateTime(wo.estimated_departure)}</Field>
          <Field label="Comandante">{wo.commander_name || "—"}</Field>
          <Field label="ANAC Piloto">{wo.pilot_anac_code || "—"}</Field>
          <Field label="Passageiros">{wo.passenger_count ?? 0}</Field>
          <Field label="Cód. ANAC Aeronave">{wo.anac_code || "—"}</Field>
        </div>
        {wo.general_notes && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <div className="text-xs font-bold tracking-wider uppercase text-slate-500 mb-1">Observações</div>
            <div className="text-sm text-slate-700 whitespace-pre-wrap">{wo.general_notes}</div>
          </div>
        )}
      </Card>

      {/* Checklist */}
      {checklist && (
        <Card className="p-5 rounded-sm border-slate-200 mb-6">
          <div className="flex justify-between items-center mb-3">
            <div>
              <div className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500">Intake</div>
              <h2 className="font-display font-bold text-xl tracking-tight">Checklist do Pouso</h2>
            </div>
            {canEditOperator && (
              <Button size="sm" variant="outline" onClick={saveChecklist} className="rounded-sm" data-testid="save-checklist-button">Salvar Checklist</Button>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {CHECKLIST.map(([k, label]) => (
              <label key={k} className="flex items-center gap-3 px-3 py-3 border border-slate-200 rounded-sm hover:bg-slate-50 cursor-pointer">
                <Checkbox
                  checked={!!checklist[k]}
                  disabled={!canEditOperator}
                  onCheckedChange={(v) => setChecklist({ ...checklist, [k]: !!v })}
                  data-testid={`checklist-${k}`}
                />
                <span className="text-sm font-medium text-slate-800">{label}</span>
              </label>
            ))}
          </div>
          <div className="mt-4">
            <Label className="text-xs font-bold tracking-wider uppercase text-slate-500">Discrepâncias / Irregularidades</Label>
            <Textarea
              value={checklist.discrepancias || ""}
              onChange={(e) => setChecklist({ ...checklist, discrepancias: e.target.value })}
              disabled={!canEditOperator}
              className="mt-1 rounded-sm min-h-20"
              data-testid="discrepancias-input"
              placeholder="Anote arranhões, fluidos, danos…"
            />
          </div>
          <div className="mt-5 pt-4 border-t border-slate-100">
            <PhotoUploader
              label="Fotos do Intake (evidência da condição na chegada)"
              value={checklist.fotos_intake || []}
              onChange={(paths) => setChecklist({ ...checklist, fotos_intake: paths })}
              disabled={!canEditOperator}
              max={12}
              testId="intake-photos"
            />
          </div>
        </Card>
      )}

      {/* Services */}
      <Card className="p-5 rounded-sm border-slate-200 mb-6">
        <div className="flex justify-between items-center mb-3">
          <div>
            <div className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500">Rampa</div>
            <h2 className="font-display font-bold text-xl tracking-tight">Serviços Executados</h2>
          </div>
          {(canEditOperator || canEditOffice) && (
            <Button size="sm" variant="outline" onClick={saveServices} className="rounded-sm" data-testid="save-services-button">Salvar Serviços</Button>
          )}
        </div>

        {(canEditOperator || canEditOffice) && (
          <div className="mb-4">
            <div className="text-xs font-bold tracking-wider uppercase text-slate-500 mb-2">Catálogo</div>
            <div className="flex flex-wrap gap-2">
              {catalogServices.map((s) => (
                <button
                  key={s.id}
                  onClick={() => addCatalogService(s)}
                  type="button"
                  className="px-3 py-2 border border-slate-300 hover:border-[#0052CC] rounded-sm text-xs font-medium text-slate-700 hover:text-[#0052CC] transition-colors"
                  data-testid={`add-service-${s.name}`}
                >
                  <Plus className="w-3 h-3 inline mr-1" /> {s.name}
                </button>
              ))}
              {catalogServices.length === 0 && (
                <span className="text-xs text-slate-400">Cadastre serviços em &quot;Cadastros → Serviços&quot;.</span>
              )}
            </div>
            <div className="flex gap-2 mt-3">
              <Input
                value={adhocName}
                onChange={(e) => setAdhocName(e.target.value)}
                placeholder="Novo serviço ad-hoc…"
                className="h-11 rounded-sm flex-1"
                data-testid="adhoc-input"
              />
              <Button type="button" onClick={addAdHocService} variant="outline" className="h-11 rounded-sm" data-testid="add-adhoc-button">
                <Plus className="w-4 h-4 mr-1" /> Adicionar
              </Button>
            </div>
          </div>
        )}

        <div className="border border-slate-200 rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left py-2 px-3 font-semibold">Serviço</th>
                <th className="text-right py-2 px-3 font-semibold w-24">Qtd.</th>
                {!isOperator && <th className="text-right py-2 px-3 font-semibold w-32">Preço Un.</th>}
                {!isOperator && <th className="text-right py-2 px-3 font-semibold w-32">Total</th>}
                {(canEditOperator || canEditOffice) && <th className="w-12"></th>}
              </tr>
            </thead>
            <tbody>
              {draftServices.length === 0 && (
                <tr><td colSpan={5} className="py-6 text-center text-slate-400 text-sm">Nenhum serviço lançado.</td></tr>
              )}
              {draftServices.map((s) => (
                <tr key={s.id} className="border-t border-slate-100" data-testid={`service-row-${s.name}`}>
                  <td className="py-2 px-3">
                    <div className="text-slate-800">{s.name}</div>
                    {s.is_ad_hoc && <div className="text-[10px] uppercase tracking-wider text-amber-600 font-bold">Ad-hoc</div>}
                  </td>
                  <td className="py-2 px-3">
                    <Input
                      type="number" min={0} step="0.5"
                      value={s.quantity}
                      onChange={(e) => setLine(s.id, { quantity: Number(e.target.value) || 0 })}
                      disabled={!(canEditOperator || canEditOffice)}
                      className="h-9 text-right rounded-sm font-mono"
                      data-testid={`service-qty-${s.name}`}
                    />
                  </td>
                  {!isOperator && (
                    <td className="py-2 px-3">
                      <Input
                        type="number" min={0} step="0.01"
                        value={s.unit_price}
                        onChange={(e) => setLine(s.id, { unit_price: Number(e.target.value) || 0 })}
                        disabled={!canEditOffice}
                        className="h-9 text-right rounded-sm font-mono"
                        data-testid={`service-price-${s.name}`}
                      />
                    </td>
                  )}
                  {!isOperator && (
                    <td className="py-2 px-3 text-right font-mono text-slate-800 tabular-nums">
                      {brl((Number(s.quantity) || 0) * (Number(s.unit_price) || 0))}
                    </td>
                  )}
                  {(canEditOperator || canEditOffice) && (
                    <td className="py-2 px-3 text-right">
                      <button onClick={() => removeLine(s.id)} className="text-slate-400 hover:text-red-600" data-testid={`remove-service-${s.name}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            {!isOperator && draftServices.length > 0 && (
              <tfoot>
                <tr className="border-t border-slate-200 bg-slate-50">
                  <td colSpan={3} className="py-3 px-3 text-right text-xs font-bold uppercase tracking-wider text-slate-600">Total</td>
                  <td className="py-3 px-3 text-right font-mono font-bold text-slate-900 tabular-nums" data-testid="services-total">{brl(total)}</td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        {isOperator && (
          <p className="text-[11px] text-slate-500 mt-2 flex items-center gap-1.5">
            <Lock className="w-3 h-3" /> Valores ficam ocultos para o perfil Operador.
          </p>
        )}
      </Card>

      {/* Movements */}
      <Card className="p-5 rounded-sm border-slate-200 mb-6">
        <div className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500">Pátio</div>
        <h2 className="font-display font-bold text-xl tracking-tight mb-3">Movimentações Temporárias</h2>

        {canEditOperator && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-4 items-end">
            <div className="md:col-span-1">
              <Label className="text-xs">Tipo</Label>
              <select
                value={movement.movement_type}
                onChange={(e) => setMovement({ ...movement, movement_type: e.target.value })}
                className="w-full h-11 border border-slate-300 rounded-sm px-2 text-sm bg-white"
                data-testid="movement-type-select"
              >
                <option value="saida_temporaria_patio">Saída Temporária Pátio</option>
                <option value="retorno_hangar">Retorno ao Hangar</option>
                <option value="reabastecimento">Reabastecimento</option>
                <option value="outra">Outra</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Data/Hora</Label>
              <Input type="datetime-local" value={movement.datetime_iso} onChange={(e) => setMovement({ ...movement, datetime_iso: e.target.value })} className="h-11 rounded-sm" data-testid="movement-dt-input" />
            </div>
            <div>
              <Label className="text-xs">Destino / Motivo</Label>
              <Input value={movement.destination_purpose} onChange={(e) => setMovement({ ...movement, destination_purpose: e.target.value })} className="h-11 rounded-sm" data-testid="movement-dest-input" />
            </div>
            <div>
              <Label className="text-xs">Retorno Estimado</Label>
              <Input type="datetime-local" value={movement.estimated_return} onChange={(e) => setMovement({ ...movement, estimated_return: e.target.value })} className="h-11 rounded-sm" />
            </div>
            <Button onClick={addMovement} variant="outline" className="h-11 rounded-sm" data-testid="add-movement-button">Adicionar</Button>
          </div>
        )}

        {wo.movements?.length > 0 ? (
          <div className="border border-slate-200 rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left py-2 px-3 font-semibold">Tipo</th>
                  <th className="text-left py-2 px-3 font-semibold">Data/Hora</th>
                  <th className="text-left py-2 px-3 font-semibold">Destino</th>
                  <th className="text-left py-2 px-3 font-semibold">Retorno</th>
                </tr>
              </thead>
              <tbody>
                {wo.movements.map((m, i) => (
                  <tr key={m.id || i} className="border-t border-slate-100">
                    <td className="py-2 px-3 text-xs uppercase tracking-wider text-slate-700">{m.movement_type}</td>
                    <td className="py-2 px-3 font-mono text-xs">{fmtDateTime(m.datetime_iso)}</td>
                    <td className="py-2 px-3 text-slate-700">{m.destination_purpose || "—"}</td>
                    <td className="py-2 px-3 font-mono text-xs">{fmtDateTime(m.estimated_return)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-sm text-slate-400">Nenhuma movimentação registrada.</div>
        )}
      </Card>

      {/* Finalize / Signatures */}
      {canEditOperator && (
        <Card className="p-5 rounded-sm border-slate-200 mb-6">
          <div className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500">Encerramento</div>
          <h2 className="font-display font-bold text-xl tracking-tight mb-3">Finalização & Assinaturas</h2>
          <Label className="text-xs font-bold tracking-wider uppercase text-slate-500">Observações de Encerramento</Label>
          <Textarea
            value={closingRemarks}
            onChange={(e) => setClosingRemarks(e.target.value)}
            placeholder="Resumo do atendimento, peças entregues, recomendações…"
            className="mt-1 mb-4 rounded-sm min-h-20"
            data-testid="closing-remarks-input"
          />
          <div className="mb-5">
            <PhotoUploader
              label="Fotos da Finalização (estado de entrega)"
              value={finalPhotos}
              onChange={setFinalPhotos}
              max={12}
              testId="final-photos"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <SignaturePad label="Assinatura Cliente / Responsável" value={clientSig} onChange={setClientSig} testId="client-signature" />
            <SignaturePad label="Assinatura Operador" value={opSig} onChange={setOpSig} testId="operator-signature" />
          </div>
          <div className="mt-5 flex justify-end">
            <Button
              onClick={finalize}
              className="h-12 bg-[#0052CC] hover:bg-[#003D99] text-white rounded-sm px-6"
              data-testid="finalize-button"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" /> Finalizar OS & Enviar para Faturamento
            </Button>
          </div>
        </Card>
      )}

      {/* Photos preview (read-only) for finalized/invoiced */}
      {!canEditOperator && ((wo.final_photos?.length || 0) > 0) && (
        <Card className="p-5 rounded-sm border-slate-200 mb-6">
          <h2 className="font-display font-bold text-xl tracking-tight mb-3">Fotos da Finalização</h2>
          <PhotoUploader value={wo.final_photos} disabled max={12} testId="final-photos-readonly" />
        </Card>
      )}

      {/* Signatures preview (read-only) */}
      {!canEditOperator && (clientSig || opSig) && (
        <Card className="p-5 rounded-sm border-slate-200 mb-6">
          <h2 className="font-display font-bold text-xl tracking-tight mb-3">Assinaturas Capturadas</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {clientSig && (
              <div>
                <div className="text-xs font-bold tracking-wider uppercase text-slate-500 mb-2">Cliente</div>
                <img src={clientSig} alt="Assinatura cliente" className="border border-slate-200 rounded-sm bg-white max-h-40" />
              </div>
            )}
            {opSig && (
              <div>
                <div className="text-xs font-bold tracking-wider uppercase text-slate-500 mb-2">Operador</div>
                <img src={opSig} alt="Assinatura operador" className="border border-slate-200 rounded-sm bg-white max-h-40" />
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-slate-500">{label}</div>
      <div className="text-sm text-slate-800 mt-0.5">{children}</div>
    </div>
  );
}
